// API Service for all dashboard features
import { apiRequest } from '@/lib/queryClient';
import { resolveApiUrl, getConfiguredApiBaseUrl } from '@/lib/apiBase';
import { authStore } from '@/lib/authStore';
import type {
  KillSwitchClearRequest,
  KillSwitchEngageRequest,
  KillSwitchState,
  KPI,
  KPIHistoryPoint,
  KPITypes,
} from '@shared/schema';
import type {
  StrategyRecommendation,
  TokenizedVenue as TokenizedVenueType,
  TokenizedVenueHealth as TokenizedVenueHealthType,
  TokenizedVenueInstrument as TokenizedVenueInstrumentType,
  RebalancePolicy,
  RebalanceRun,
  RebalancePolicyListResponse,
  RebalanceRunListResponse,
  RebalanceTarget,
  RebalanceSchedule,
} from '@/types';
import type {
  AgentCreateRequest,
  AgentCreateResponse,
  AgentControlRoomHaltResponse,
  AgentControlRoomSummary,
  AgentHaltRequest,
  AgentActionPreviewCancelRequest,
  AgentActionPreviewResponse,
  AgentActionPreviewSubmitRequest,
  AgentCapabilityManifest,
  AgentProposalLifecycleRequest,
  AgentProviderCallRequest,
  AgentProviderCallResponse,
  AgentProposal,
  AgentRunRequest,
  AgentRunResponse,
  AgentRunStepsResponse,
  AgentRunTimelineResponse,
  TradingAgent,
} from '@/data/agentControlRoom';

export interface ApprovalRecord {
  approvalUuid: string;
  type: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  notes?: string | null;
  requestedBy?: string | null;
  requestedAt: string;
  decidedAt?: string | null;
  approver?: string | null;
  action?: string;
  approvalType?: string;
  orgId?: string | null;
  portfolioId?: string | null;
  hashId?: string;
  expectedStatus?: string;
}

export interface ApprovalsListResponse {
  approvals: ApprovalRecord[];
}

export interface ComplianceViolation {
  id: string;
  violationUuid?: string | null;
  rule: string;
  severity: string;
  description: string;
  occurredAt: string;
  resolvedAt?: string | null;
  portfolioId?: string | null;
  status?: string;
}

export interface ComplianceViolationsResponse {
  portfolio?: {
    violations: ComplianceViolation[];
    summary?: {
      open?: number;
      acknowledged?: number;
      resolved?: number;
    };
  };
  tenant?: {
    violations?: ComplianceViolation[];
  };
  organization?: {
    violations?: ComplianceViolation[];
  };
}

export interface TenantWalletSummary {
  watchOnlyConnections: number;
  totalValueUsd: number;
  asOf?: string | null;
}

export interface WalletCounterpartyRisk {
  counterpartyHash: string;
  label: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  riskScore: number;
  summary: string;
  totalMatches: number;
  lastDetected?: string | null;
  guidance?: string | null;
}

export interface NonCustodialDiagnosticsSummary {
  totalConnections: number;
  exchangeConnections: number;
  violations: number;
  compliant: number;
  manifestVersion: string;
  generatedAt: string;
}

export interface NonCustodialAuditChainRef {
  lastSeqId?: number | null;
  lastEventAt?: string | null;
}

export interface NonCustodialDiagnosticsItem {
  connectionId: string;
  type: string;
  label: string | null;
  provider: string | null;
  status: string;
  connectionControlMode: string;
  withdrawalScopeAllowed: boolean;
  policyStatus: 'compliant' | 'violation' | string;
  policyReason: string;
  blockedBy: string[];
  observedScopes: string[];
  missingRequiredScopes: string[];
  providerProfile: string | null;
  manifestVersion: string;
  updatedAt?: string | null;
  auditChain?: NonCustodialAuditChainRef;
}

export interface NonCustodialDiagnosticsResponse {
  summary: NonCustodialDiagnosticsSummary;
  items: NonCustodialDiagnosticsItem[];
}

export interface NonCustodialCapabilitiesResponse {
  manifestVersion: string;
  providers: string[];
}

export interface NonCustodialExportResponse {
  summary: {
    total: number;
    violations: number;
    compliant: number;
    manifestVersion: string;
    generatedAt: string;
  };
  items: NonCustodialDiagnosticsItem[];
  checksum: string;
}

export interface BotEvent {
  id: number;
  ts: string | null;
  type: string;
  data: {
    symbol?: string;
    side?: string;
    code?: string;
    raw_code?: string;
    message?: string;
    reasons?: string[];
  };
}

export interface BotLiveData {
  connected: boolean;
  exchange?: string;
  pair?: string;
  lastApiCall?: string;
  latencyMs?: number;
  currentPrice?: {
    bid?: number;
    ask?: number;
    last?: number;
    timestamp?: string;
  };
  balance?: {
    base?: {
      currency: string;
      free: number;
      used: number;
      total: number;
    };
    quote?: {
      currency: string;
      free: number;
      used: number;
      total: number;
    };
  };
  openOrdersCount?: number;
  recentTrades?: Array<{
    id: string;
    side: string;
    price: number;
    amount: number;
    timestamp: string;
    selectedVenue?: string;
    fallbackReason?: string;
    decisionLatencyMs?: number;
  }>;
  error?: string;
}

export interface TenantSummary {
  id: string;
  name: string;
  status?: string;
  portfolios?: number;
  activeBots?: number;
  connectedVenues?: number;
  lastActivity?: string;
  pendingApprovals?: number;
  region?: string;
  legalEntity?: string;
  usage?: Record<string, unknown>;
  walletSummary?: TenantWalletSummary | null;
}

export interface TenantListResponse {
  tenants: TenantSummary[];
}

export interface TenantFeaturesResponse {
  tenant_id: string;
  is_design_partner: boolean;
  paper_only: boolean;
  kpd_featureset_v1: boolean;
  console_enabled: boolean;
  strategy_admin_enabled: boolean;
}

export interface AdminUserRecord {
  id: string;
  name: string;
  email: string;
  username?: string;
  role:
    | 'org_owner'
    | 'trading_operator'
    | 'analyst'
    | 'auditor'
    | 'rugira_platform_admin'
    | 'rugira_support'
    | 'rugira_compliance_admin'
    | string;
  status: 'active' | 'suspended' | 'inactive' | string;
  subscription: 'Free' | 'Professional' | 'Enterprise' | string;
  country?: string;
  orgsCount?: number;
  totalVolumeChf: number;
  totalTrades: number;
  lastLogin: string;
  createdAt?: string;
}

export interface AdminSystemConfigRecord {
  key: string;
  category: 'security' | 'trading' | 'system' | 'api' | string;
  value: string;
  description?: string;
  updatedAt?: string;
}

export type UserNotificationPreferences = Record<string, boolean>;

export interface UserProfileResponse {
  userId: string;
  displayName: string | null;
  language: string | null;
  timezone: string | null;
  phone: string | null;
  notifications: UserNotificationPreferences;
  updatedAt: string;
}

export interface UserProfileUpdateRequest {
  displayName?: string | null;
  language?: string | null;
  timezone?: string | null;
  phone?: string | null;
  notifications?: UserNotificationPreferences;
}

export interface UserSessionRecord {
  sessionId: number;
  userId: string;
  orgId: string;
  portfolioId: string;
  mode: string;
  lastUsedAt: string;
}

export type ReportVisibility = 'private' | 'team' | 'tenant';

export interface ReportSummary {
  reportId: string;
  name: string;
  visibility: ReportVisibility;
  ownerUserId: string | null;
  orgId: string | null;
  description: string | null;
  retentionDays: number;
  lastGeneratedAt: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface ReportDefinition extends ReportSummary {
  definition: Record<string, unknown>;
}

export interface ReportResultSummary {
  resultId: string;
  reportId: string | null;
  name: string;
  status: 'queued' | 'completed' | 'failed';
  visibility: ReportVisibility;
  ownerUserId: string | null;
  orgId: string | null;
  generatedAt: string;
  expiresAt: string;
}

export interface ReportResult extends ReportResultSummary {
  definition: Record<string, unknown>;
  output?: Record<string, unknown> | null;
}

export interface ReportListResponse {
  reports: ReportSummary[];
}

export interface ReportCreateResponse {
  report: ReportDefinition;
}

export interface ReportResultListResponse {
  results: ReportResultSummary[];
}

export interface ReportGenerateRequestPayload {
  reportId?: string | null;
  definition?: Record<string, unknown> | null;
  name?: string | null;
  visibility?: ReportVisibility | null;
  orgId?: string | null;
  retentionDays?: number | null;
}

export interface ReportGenerateResponsePayload {
  jobId: string;
  status: 'queued' | 'started';
}

export type SettingsNamespace = 'trading' | 'notifications' | 'integrations';

export interface SettingsLayer {
  values: Record<string, unknown>;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface SettingsEffective {
  values: Record<string, unknown>;
  source: 'tenant' | 'org' | 'user' | 'default';
}

export interface SettingsResponse {
  effective: Partial<Record<SettingsNamespace, SettingsEffective>>;
  tenant: Partial<Record<SettingsNamespace, SettingsLayer>>;
  org: Partial<Record<SettingsNamespace, SettingsLayer>>;
  user: Partial<Record<SettingsNamespace, SettingsLayer>>;
}

export interface SettingsUpdatePayload {
  scope: 'tenant' | 'org' | 'user';
  namespace: SettingsNamespace;
  values: Record<string, unknown>;
  orgId?: string | null;
  userId?: string | null;
  dualConfirm?: boolean;
}

export interface SettingsUpdateRequestPayload {
  updates: SettingsUpdatePayload[];
}

export interface SettingsUpdateResultPayload {
  scope: 'tenant' | 'org' | 'user';
  namespace: SettingsNamespace;
  values: Record<string, unknown>;
  orgId?: string | null;
  userId?: string | null;
}

export interface SettingsUpdateResponse {
  updated: SettingsUpdateResultPayload[];
}

const getApiBase = () => getConfiguredApiBaseUrl();

const buildUrl = (endpoint: string): string => resolveApiUrl(endpoint, getApiBase());

function normalizeAgentRunResponse(run: AgentRunResponse, routeHint = '/agents'): AgentRunResponse {
  const route =
    run.capabilityManifest?.route ??
    (typeof routeHint === 'string' && routeHint.trim() ? routeHint : '/agents');
  const capabilityManifest = run.capabilityManifest ?? {
    route,
    tenantSlug: run.tenantSlug,
    agentId: run.agentId ?? null,
    executionMode: run.executionMode,
    allowedCapabilities: ['read_context', 'analyze', 'draft'],
    blockedCapabilities: ['production_action'],
    allowedTools: [],
    blockedTools: ['exchange_order_router', 'wallet_signing', 'key_service_read'],
    policyNotes: [
      'Backend response omitted capability manifest; the UI is showing a blocked scope.',
      'No direct provider-to-order path is available from the assistant surface.',
    ],
  };
  const executionDecision = run.executionDecision ?? {
    status: run.executionMode === 'proposal_draft' ? 'draft_only' : 'allowed',
    reason:
      run.executionMode === 'proposal_draft'
        ? 'Proposal draft mode permits evidence generation only; live execution remains blocked.'
        : 'Brief-only mode permits read and analysis evidence only.',
    requiredCapability: run.executionMode === 'proposal_draft' ? 'draft' : 'analyze',
    mode: run.executionMode,
    canSubmit: false,
  };
  const contractWarnings = [
    ...(run.contractWarnings ?? []),
    ...(run.capabilityManifest
      ? []
      : ['Backend response omitted capability manifest; UI is showing a safe blocked scope.']),
    ...(run.executionDecision
      ? []
      : ['Backend response omitted execution decision; UI is showing a safe blocked decision.']),
    ...(Array.isArray(run.actionPreviews)
      ? []
      : ['Backend response omitted action previews; no frontend preview evidence was generated.']),
    ...(Array.isArray(run.artifacts)
      ? []
      : ['Backend response omitted artifacts; no frontend artifact evidence was generated.']),
    ...(Array.isArray(run.timeline)
      ? []
      : ['Backend response omitted timeline events; no frontend timeline evidence was generated.']),
  ];

  return {
    ...run,
    capabilityManifest,
    executionDecision,
    actionPreviews: Array.isArray(run.actionPreviews) ? run.actionPreviews : [],
    artifacts: Array.isArray(run.artifacts) ? run.artifacts : [],
    timeline: Array.isArray(run.timeline) ? run.timeline : [],
    contractWarnings,
  };
}

async function fetchAPI<T>(endpoint: string): Promise<T> {
  const response = await apiRequest('GET', buildUrl(endpoint));
  return response.json();
}

async function postAPI<T>(
  endpoint: string,
  data?: any,
  headers?: Record<string, string>,
): Promise<T> {
  const response = await apiRequest('POST', buildUrl(endpoint), data, headers);
  return response.json();
}

async function patchAPI<T>(endpoint: string, data: any): Promise<T> {
  const response = await apiRequest('PATCH', buildUrl(endpoint), data);
  return response.json();
}

async function deleteAPI(endpoint: string): Promise<void> {
  await apiRequest('DELETE', buildUrl(endpoint));
}

// Automations API - calls API Gateway which queries PostgreSQL bots table
export const automationAPI = {
  getAll: async () => {
    const response = await fetchAPI<{ items: any[]; bots?: any[] }>('/api/automations');
    // API returns { items: [...], bots: [...] }, extract the items array
    return response.items || [];
  },
  getById: (id: string) => fetchAPI<any>(`/api/automations/${id}`),
  start: (id: string, aiSuggestion?: StrategyRecommendation | null) =>
    postAPI<any>('/api/automations/start', { botId: id, aiSuggestion }),
  stop: (id: string) => postAPI<any>('/api/automations/stop', { botId: id }),
  create: (data: any) => postAPI<any>('/api/automations', data),
  getLiveData: (id: string) => fetchAPI<BotLiveData>(`/api/bots/${id}/live-data`),
  getEvents: (id: string, type?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const suffix = qs ? '?' + qs : '';
    return fetchAPI<{ items: BotEvent[] }>(`/api/bots/${id}/events` + suffix);
  },
  delete: (id: string) => deleteAPI(`/api/automations/${id}`),
};

export const botAPI = automationAPI;

export const agentControlRoomAPI = {
  getSummary: () => fetchAPI<AgentControlRoomSummary>('/api/agent-control-room/summary'),
  getCapabilities: (params?: { route?: string; agentId?: string; executionMode?: string }) => {
    const search = new URLSearchParams();
    if (params?.route) search.set('route', params.route);
    if (params?.agentId) search.set('agent_id', params.agentId);
    if (params?.executionMode) search.set('execution_mode', params.executionMode);
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return fetchAPI<AgentCapabilityManifest>(`/api/agent-control-room/capabilities${suffix}`);
  },
  getAgents: async () => {
    const response = await fetchAPI<{ items: TradingAgent[] }>('/api/agent-control-room/agents');
    return response.items || [];
  },
  getProposals: async () => {
    const response = await fetchAPI<{ items: AgentProposal[] }>(
      '/api/agent-control-room/proposals',
    );
    return response.items || [];
  },
  getProposal: (proposalId: string) =>
    fetchAPI<AgentProposal>(`/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}`),
  createAgent: (payload: AgentCreateRequest) =>
    postAPI<AgentCreateResponse>('/api/agent-control-room/agents', payload),
  createAgentRun: async (payload: AgentRunRequest, idempotencyKey?: string) =>
    normalizeAgentRunResponse(
      await postAPI<AgentRunResponse>(
        '/api/agent-control-room/runs',
        payload,
        idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      ),
      typeof payload.clientContext?.route === 'string' ? payload.clientContext.route : '/agents',
    ),
  getAgentRun: (runId: string) =>
    fetchAPI<AgentRunResponse>(`/api/agent-control-room/runs/${encodeURIComponent(runId)}`).then(
      (run) => normalizeAgentRunResponse(run, run.capabilityManifest?.route ?? '/agents'),
    ),
  getAgentRunSteps: (runId: string) =>
    fetchAPI<AgentRunStepsResponse>(
      `/api/agent-control-room/runs/${encodeURIComponent(runId)}/steps`,
    ),
  getAgentRunTimeline: (runId: string) =>
    fetchAPI<AgentRunTimelineResponse>(
      `/api/agent-control-room/runs/${encodeURIComponent(runId)}/timeline`,
    ),
  cancelAgentRun: (runId: string) =>
    postAPI<AgentRunResponse>(
      `/api/agent-control-room/runs/${encodeURIComponent(runId)}/cancel`,
      {},
    ).then((run) => normalizeAgentRunResponse(run, run.capabilityManifest?.route ?? '/agents')),
  submitActionPreview: async (previewId: string, payload: AgentActionPreviewSubmitRequest) => {
    const requestPayload = {
      idempotencyKey: payload.idempotencyKey,
      comment: payload.comment ?? payload.note ?? null,
    };
    const response = await postAPI<AgentActionPreviewResponse>(
      `/api/agent-control-room/action-previews/${encodeURIComponent(previewId)}/submit`,
      requestPayload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    );
    return {
      ...response,
      run: normalizeAgentRunResponse(
        response.run,
        response.run.capabilityManifest?.route ?? '/agents',
      ),
    };
  },
  cancelActionPreview: async (previewId: string, payload: AgentActionPreviewCancelRequest = {}) => {
    const requestPayload = {
      idempotencyKey: payload.idempotencyKey,
      reason: payload.reason ?? null,
    };
    const response = await postAPI<AgentActionPreviewResponse>(
      `/api/agent-control-room/action-previews/${encodeURIComponent(previewId)}/cancel`,
      requestPayload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    );
    return {
      ...response,
      run: normalizeAgentRunResponse(
        response.run,
        response.run.capabilityManifest?.route ?? '/agents',
      ),
    };
  },
  evaluateProposalRisk: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/evaluate-risk`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  submitProposalForApproval: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/submit-for-approval`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  approveProposal: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/approve`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  rejectProposal: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/reject`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  submitProposalToOms: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/submit-to-oms`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  cancelProposal: (proposalId: string, payload: AgentProposalLifecycleRequest = {}) =>
    postAPI<AgentProposal>(
      `/api/agent-control-room/proposals/${encodeURIComponent(proposalId)}/cancel`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
  createProviderCall: (payload: AgentProviderCallRequest) =>
    postAPI<AgentProviderCallResponse>('/api/agent-control-room/provider-calls', payload),
  haltAgent: (agentId: string, payload: AgentHaltRequest) =>
    postAPI<AgentControlRoomHaltResponse>(
      `/api/agent-control-room/agents/${encodeURIComponent(agentId)}/halt`,
      payload,
      payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    ),
};

export const tenantAPI = {
  list: () => fetchAPI<TenantListResponse>('/api/tenants'),
  getFeatures: (tenantId: string) =>
    fetchAPI<TenantFeaturesResponse>(`/api/tenants/${tenantId}/features`),
};

// Organization and Portfolio API for scope management
export interface OrganizationResponse {
  id: string;
  name: string;
  status: string;
  memberCount?: number;
  createdAt: string;
}

export interface PortfolioResponse {
  portfolio_id: string;
  org_id: string;
  name: string;
  risk_profile: string;
  default_mode: string;
  live_enabled: boolean;
}

// Safe fetch that doesn't trigger auth failure handling - for use during app initialization
// when auth may not be complete yet
const safeFetch = async <T>(endpoint: string): Promise<T | null> => {
  try {
    const url = buildUrl(endpoint);
    const headers: Record<string, string> = {};
    const token = authStore.getToken?.() || import.meta.env.VITE_DEV_TOKEN;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { credentials: 'include', headers });
    if (!response.ok) {
      // Silently return null for any error - don't trigger auth logout
      return null;
    }
    return response.json();
  } catch {
    return null;
  }
};

export const organizationAPI = {
  // Use safeFetch to avoid triggering auth logout loop during app initialization
  list: async (): Promise<OrganizationResponse[]> => {
    const result = await safeFetch<OrganizationResponse[]>('/api/admin/orgs');
    return result ?? [];
  },
  getPortfolios: async (orgId: string): Promise<PortfolioResponse[]> => {
    const result = await safeFetch<PortfolioResponse[]>(`/api/orgs/${orgId}/portfolios`);
    return result ?? [];
  },
};

export const consoleAPI = {
  getState: (pair?: string) =>
    fetchAPI<any>(
      pair ? `/api/console/state?pair=${encodeURIComponent(pair)}` : '/api/console/state',
    ),
  simulateTicket: (payload: any) => postAPI('/api/console/ticket/simulate', payload),
};

// Connections (legacy venues/exchanges compatibility retained)
export const exchangeAPI = {
  getAll: () => fetchAPI<any[]>('/api/exchanges'),
  getById: (id: string) => fetchAPI<any>(`/api/exchanges/${id}`),
  connect: (data: any) => postAPI<any>('/api/exchanges', data),
  disconnect: (id: string) => postAPI<any>(`/api/exchanges/${id}/disconnect`),
};

// Exchange Accounts (admin API for testing/creating exchange credentials)
export interface ExchangeAccountCredentials {
  apiKey: string;
  apiSecret: string;
  passphrase?: string;
  sandbox?: boolean;
}

export interface CreateExchangeAccountPayload {
  name: string;
  exchange: string;
  credentials: ExchangeAccountCredentials;
  description?: string;
}

export interface ExchangeAccount {
  id: string;
  tenant_id: string;
  name: string;
  exchange: string;
  status: string;
  last_tested?: string | null;
  test_status?: string | null;
  created_at: string;
  updated_at: string;
}

type ExchangeAccountListResponse =
  | ExchangeAccount[]
  | {
      data?: ExchangeAccount[];
      items?: ExchangeAccount[];
    };

const normalizeExchangeAccountList = (response: ExchangeAccountListResponse): ExchangeAccount[] => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  return [];
};

export interface ExchangeHolding {
  currency: string;
  free: number;
  used: number;
  total: number;
  chf_value?: number;
}

export interface ExchangeSyncResult {
  success: boolean;
  account_id: string;
  exchange: string;
  synced_at: string;
  portfolio: {
    total_chf: number;
    available_chf: number;
    in_orders_chf: number;
    change_24h_percent?: number;
    holdings: ExchangeHolding[];
  };
  fees: {
    maker: number;
    taker: number;
    tier?: string;
  };
  open_orders_count: number;
  active_bots_count: number;
  api_permissions: {
    read: boolean;
    trade: boolean;
    withdraw: boolean;
    ip_restricted?: boolean;
  };
}

export interface ExchangeTestCredentialsPayload {
  exchange: string;
  credentials: ExchangeAccountCredentials;
  sandbox?: boolean;
}

export interface ExchangeTestConnectionResult {
  success: boolean;
  exchange: string;
  tested_at: string;
  capabilities?: {
    trading: boolean;
    fetchBalance: boolean;
    fetchOrders: boolean;
    fetchTicker: boolean;
    sandbox: boolean;
  };
  error?: string;
  error_code?: string;
}

export const exchangeAccountsAPI = {
  create: (payload: CreateExchangeAccountPayload) =>
    postAPI<ExchangeAccount>('/api/exchange-accounts', payload),
  list: async () =>
    normalizeExchangeAccountList(
      await fetchAPI<ExchangeAccountListResponse>('/api/exchange-accounts'),
    ),
  getById: (id: string) => fetchAPI<ExchangeAccount>(`/api/exchange-accounts/${id}`),
  testCredentials: (payload: ExchangeTestCredentialsPayload) =>
    postAPI<ExchangeTestConnectionResult>('/api/exchange-accounts/test-connection', payload),
  test: async (id: string): Promise<{ ok: boolean; error?: string }> => {
    const result = await postAPI<{ success: boolean; error?: string }>(
      `/api/exchange-accounts/${id}/test`,
      {},
    );
    return { ok: result.success, error: result.error };
  },
  sync: (id: string) => postAPI<ExchangeSyncResult>(`/api/exchange-accounts/${id}/sync`, {}),
  delete: (id: string) => deleteAPI(`/api/exchange-accounts/${id}`),
};

export interface ConnectorStatus {
  exchange: string;
  enabled: boolean;
  registered: boolean;
  success_count: number;
  failure_count: number;
  last_success_at?: string | null;
  last_error_at?: string | null;
  last_error?: string | null;
}

export const connectorsAPI = {
  listStatus: () => fetchAPI<ConnectorStatus[]>('/api/oms/connectors/status'),
};

export type TokenizedVenueInstrument = TokenizedVenueInstrumentType;
export type TokenizedVenueHealth = TokenizedVenueHealthType;
export type TokenizedVenue = TokenizedVenueType;

export interface TokenizedVenueListResponse {
  venues: TokenizedVenue[];
}

export interface TokenizedVenueHealthSnapshot {
  generatedAt: string;
  venues: TokenizedVenueHealth[];
}

export const tokenizedVenuesAPI = {
  list: () => fetchAPI<TokenizedVenueListResponse>('/api/venues/tokenized'),
  health: () => fetchAPI<TokenizedVenueHealthSnapshot>('/api/venues/tokenized/health'),
};

export type {
  RebalancePolicy,
  RebalanceRun,
  RebalancePolicyListResponse,
  RebalanceRunListResponse,
} from '@/types';

export interface RebalancePolicyCreateRequest {
  name: string;
  portfolioId: string;
  mode?: 'paper' | 'live';
  driftThresholdPct?: number;
  minTradeValue?: number | null;
  targets: RebalanceTarget[];
  schedule?: RebalanceSchedule | null;
  notes?: string | null;
}

export type RebalancePolicyUpdateRequest = Partial<RebalancePolicyCreateRequest> & {
  status?: 'active' | 'paused' | 'archived';
};

export interface RebalanceRunCreateRequest {
  policyId: string;
  trigger?: string | null;
}

export const rebalancerAPI = {
  listPolicies: () => fetchAPI<RebalancePolicyListResponse>('/api/rebalancer/policies'),
  createPolicy: (payload: RebalancePolicyCreateRequest) =>
    postAPI<RebalancePolicy>('/api/rebalancer/policies', payload),
  updatePolicy: (policyId: string, payload: RebalancePolicyUpdateRequest) =>
    patchAPI<RebalancePolicy>(`/api/rebalancer/policies/${policyId}`, payload),
  listRuns: () => fetchAPI<RebalanceRunListResponse>('/api/rebalancer/runs'),
  triggerRun: (payload: RebalanceRunCreateRequest) =>
    postAPI<RebalanceRun>('/api/rebalancer/runs', payload),
};

export interface ProviderHealthReport {
  provider: string;
  chain: string;
  status: string;
  latency_ms?: number;
  error?: string | null;
  checked_at?: string;
}

export interface IntegrationSnapshot<T = Record<string, unknown>> {
  ok: boolean;
  summary?: string;
  error?: string;
  data?: T;
}

export interface SandboxStatusResponse {
  generated_at: string;
  providers: IntegrationSnapshot<{ reports: ProviderHealthReport[] }>;
  exchanges: IntegrationSnapshot<{ connectors: ConnectorStatus[] }>;
  chainalysis: IntegrationSnapshot<{ adapters?: Record<string, unknown>; kyt_enabled?: boolean }>;
  billing: IntegrationSnapshot<{
    health?: Record<string, unknown>;
    entitlements?: Record<string, unknown> | null;
  }>;
  tax_exporter: IntegrationSnapshot<{ health?: Record<string, unknown> }>;
  orchestrator: IntegrationSnapshot<{
    health?: Record<string, unknown>;
    sqs?: Record<string, { visible: number; inflight: number }>;
  }>;
}

export const statusAPI = {
  getIntegrations: () => fetchAPI<SandboxStatusResponse>('/api/status/integrations'),
};

export const connectionsAPI = {
  list: async (params?: { type?: string; includeInactive?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.includeInactive) query.set('includeInactive', String(params.includeInactive));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetchAPI<any[]>(`/api/connections${suffix}`);
    if (!Array.isArray(response)) {
      return [];
    }
    return response.map((connection) => {
      const connectionUuid =
        connection?.connectionUuid ??
        connection?.connection_uuid ??
        connection?.uuid ??
        (connection?.id != null ? String(connection.id) : undefined);
      const normalizedId =
        connectionUuid ??
        (connection?.id != null
          ? String(connection.id)
          : `connection-${Math.random().toString(36).slice(2)}`);
      return {
        ...connection,
        connectionUuid: connectionUuid ?? normalizedId,
        legacyId: connection?.id ?? connection?.legacyId ?? null,
        id: normalizedId,
      };
    });
  },
  // Convenience methods for fetching connections by type
  listTokenized: () => connectionsAPI.list({ type: 'tokenized' }),
  listIssuerPlatforms: () => connectionsAPI.list({ type: 'issuer_platform' }),
  listExchanges: () => connectionsAPI.list({ type: 'exchange' }),
  listWallets: () => connectionsAPI.list({ type: 'wallet' }),
  create: (payload: any) => postAPI<any>('/api/connections', payload),
  update: (id: string, payload: any) => patchAPI<any>(`/api/connections/${id}`, payload),
  remove: (id: string) => deleteAPI(`/api/connections/${id}`),
  listBalances: (id: string) => fetchAPI<any[]>(`/api/connections/${id}/balances`),
  listTransactions: (id: string, params?: { limit?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return fetchAPI<any[]>(`/api/connections/${id}/transactions${suffix}`);
  },
  listRisk: (id: string) => fetchAPI<WalletCounterpartyRisk[]>(`/api/connections/${id}/risk`),
  triggerSync: (id: string) => postAPI<any>(`/api/connections/${id}/sync`, {}),
  listSyncRuns: (id: string) => fetchAPI<any[]>(`/api/connections/${id}/sync`),
  getSyncRun: (id: string, runId: string) => fetchAPI<any>(`/api/connections/${id}/sync/${runId}`),
  getNonCustodialCapabilities: () =>
    fetchAPI<NonCustodialCapabilitiesResponse>('/api/connections/non-custodial/capabilities'),
  getNonCustodialDiagnostics: (params?: { includeArchived?: boolean }) => {
    const query = new URLSearchParams();
    if (typeof params?.includeArchived === 'boolean') {
      query.set('includeArchived', String(params.includeArchived));
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return fetchAPI<NonCustodialDiagnosticsResponse>(
      `/api/connections/non-custodial/diagnostics${suffix}`,
    );
  },
  exportNonCustodialControls: (params?: {
    onlyViolations?: boolean;
    includeArchived?: boolean;
  }) => {
    const query = new URLSearchParams({ format: 'json' });
    if (typeof params?.onlyViolations === 'boolean') {
      query.set('onlyViolations', String(params.onlyViolations));
    }
    if (typeof params?.includeArchived === 'boolean') {
      query.set('includeArchived', String(params.includeArchived));
    }
    return fetchAPI<NonCustodialExportResponse>(
      `/api/connections/non-custodial/export?${query.toString()}`,
    );
  },
  downloadNonCustodialControlsCsv: async (params?: {
    onlyViolations?: boolean;
    includeArchived?: boolean;
  }): Promise<{ csv: string; checksum: string | null }> => {
    const query = new URLSearchParams({ format: 'csv' });
    if (typeof params?.onlyViolations === 'boolean') {
      query.set('onlyViolations', String(params.onlyViolations));
    }
    if (typeof params?.includeArchived === 'boolean') {
      query.set('includeArchived', String(params.includeArchived));
    }
    const response = await apiRequest(
      'GET',
      buildUrl(`/api/connections/non-custodial/export?${query.toString()}`),
    );
    return {
      csv: await response.text(),
      checksum: response.headers.get('x-file-sha256'),
    };
  },
};

// Strategies
export const strategyAPI = {
  getAll: () => fetchAPI<any[]>('/api/strategies'),
  getById: (id: number | string) => fetchAPI<any>(`/api/strategies/${id}`),
  create: (data: any) => postAPI<any>('/api/strategies', data),
  update: (id: number | string, data: any) => patchAPI<any>(`/api/strategies/${id}`, data),
  delete: (id: number | string) => deleteAPI(`/api/strategies/${id}`),
};

// Backtests
export const backtestAPI = {
  getAll: () => fetchAPI<any[]>('/api/backtests'),
  create: (data: any) => postAPI<any>('/api/backtests', data),
  getResults: (id: string) => fetchAPI<any>(`/api/backtests/${id}/results`),
  compare: async (ids: string[]) => {
    if (!ids?.length) {
      return [];
    }
    try {
      return await postAPI<any>('/api/backtests/compare', { ids });
    } catch (error) {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            return await fetchAPI<any>(`/api/backtests/${id}/results`);
          } catch {
            try {
              return await fetchAPI<any>(`/api/backtests/${id}`);
            } catch {
              return null;
            }
          }
        }),
      );
      return results.filter(Boolean);
    }
  },
};

// Market Data
export interface OHLCVCandle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OHLCVResponse {
  symbol: string;
  interval: string;
  exchange: string;
  candles: OHLCVCandle[];
}

export interface OHLCVParams {
  exchange: string;
  symbol: string;
  interval: string;
  start: string;
  end: string;
  limit?: number;
}

export const marketDataAPI = {
  getOHLCV: (params: OHLCVParams): Promise<OHLCVResponse> => {
    const query = new URLSearchParams({
      exchange: params.exchange,
      symbol: params.symbol,
      interval: params.interval,
      start: params.start,
      end: params.end,
      ...(params.limit ? { limit: String(params.limit) } : {}),
    });
    return fetchAPI<OHLCVResponse>(`/api/market-data/ohlcv?${query.toString()}`);
  },
};

// Trading Pairs
export interface TradingPair {
  id: string;
  exchange: string;
  symbol: string;
  baseCurrency: string;
  quoteCurrency: string;
  venueSymbol: string;
  isActive: boolean;
  minOrderSize?: number | null;
  maxOrderSize?: number | null;
  minNotional?: number | null;
  pricePrecision?: number | null;
  quantityPrecision?: number | null;
  takerFee?: number | null;
  makerFee?: number | null;
  lastSyncedAt?: string;
}

export interface TradingPairsResponse {
  exchange: string;
  pairs: TradingPair[];
  total: number;
  lastSyncedAt?: string | null;
  isStale?: boolean;
  syncStatus?: 'success' | 'failed' | 'in_progress' | null;
  syncError?: string | null;
}

export interface TradingPairsParams {
  quote?: string;
  base?: string;
  search?: string;
  active?: boolean;
  limit?: number;
  page?: number;
}

export const tradingPairsAPI = {
  // Get all trading pairs for an exchange
  getByExchange: (exchange: string, params?: TradingPairsParams): Promise<TradingPairsResponse> => {
    const query = new URLSearchParams();
    if (params?.quote) query.set('quote', params.quote);
    if (params?.base) query.set('base', params.base);
    if (params?.search) query.set('search', params.search);
    if (params?.active !== undefined) query.set('active', String(params.active));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.page) query.set('page', String(params.page));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return fetchAPI<TradingPairsResponse>(`/api/exchanges/${exchange}/pairs${suffix}`);
  },

  // Search trading pairs (for autocomplete)
  search: (exchange: string, query: string, limit?: number): Promise<TradingPairsResponse> => {
    const params = new URLSearchParams({ q: query });
    if (limit) params.set('limit', String(limit));
    return fetchAPI<TradingPairsResponse>(
      `/api/exchanges/${exchange}/pairs/search?${params.toString()}`,
    );
  },

  // Get quote currencies for an exchange
  getQuoteCurrencies: (
    exchange: string,
  ): Promise<{ exchange: string; quoteCurrencies: string[] }> => {
    return fetchAPI<{ exchange: string; quoteCurrencies: string[] }>(
      `/api/exchanges/${exchange}/quote-currencies`,
    );
  },

  // Get supported exchanges
  getSupportedExchanges: (): Promise<{ exchanges: string[]; total: number }> => {
    return fetchAPI<{ exchanges: string[]; total: number }>('/api/exchanges/supported');
  },
};

// Monitoring
export const monitoringAPI = {
  getAlerts: () => fetchAPI<any>('/api/monitoring/alerts'),
  getMetrics: () => fetchAPI<any>('/api/monitoring/metrics'),
  acknowledgeAlert: (id: string) => postAPI<any>(`/api/monitoring/alerts/${id}/ack`),
  resolveAlert: (id: string) => postAPI<any>(`/api/monitoring/alerts/${id}/resolve`),
};

// Compliance & approvals
export const complianceAPI = {
  getReports: () => fetchAPI<any>('/api/compliance/reports'),
  getViolations: () => fetchAPI<ComplianceViolationsResponse>('/api/compliance/violations'),
  generateReport: (type: string) => postAPI<any>('/api/compliance/reports', { type }),
};

export const approvalsAPI = {
  list: async (): Promise<ApprovalsListResponse> => {
    const response = await fetchAPI<any>('/api/approvals');
    const records = Array.isArray(response)
      ? response
      : Array.isArray(response?.approvals)
        ? response.approvals
        : [];

    const approvals: ApprovalRecord[] = records.map((item: any) => {
      const approvalUuid =
        item.approvalUuid ??
        item.approval_uuid ??
        item.uuid ??
        `approval-${Math.random().toString(36).slice(2)}`;
      return {
        approvalUuid,
        type: item.approval_type ?? item.action ?? 'approval',
        status: item.status ?? 'pending',
        notes: item.decision_note ?? null,
        requestedBy: item.requester ?? null,
        requestedAt: item.created_at ?? new Date().toISOString(),
        decidedAt: item.decided_at ?? null,
        approver: item.approver ?? null,
        action: item.action ?? null,
        approvalType: item.approval_type ?? null,
        orgId: item.org_id ?? null,
        portfolioId: item.portfolio_id ?? null,
        hashId: item.hash_id ?? null,
        expectedStatus: item.status ?? 'pending',
      };
    });

    return { approvals };
  },
  decide: (
    id: string,
    data: { status: 'approved' | 'rejected'; expectedStatus: string; note?: string },
  ) =>
    patchAPI<any>(`/api/approvals/${id}`, {
      status: data.status,
      expectedStatus: data.expectedStatus,
      note: data.note,
    }),
};

// Portfolio & markets
export const portfolioAPI = {
  getOverview: () => fetchAPI<any>('/api/portfolio'),
  getPositions: () => fetchAPI<any[]>('/api/portfolio/positions'),
  getPerformance: () => fetchAPI<any>('/api/portfolio/performance'),
};

export const marketAPI = {
  getOverview: () => fetchAPI<any>('/api/market'),
  getPrices: () => fetchAPI<any>('/api/market/prices'),
  getTrends: () => fetchAPI<any>('/api/market/trends'),
};

// User & admin
export const userAPI = {
  getProfile: () => fetchAPI<UserProfileResponse>('/api/user/profile'),
  updateProfile: (data: UserProfileUpdateRequest) =>
    patchAPI<UserProfileResponse>('/api/user/profile', data),
  getSessions: () => fetchAPI<UserSessionRecord[]>('/api/user/sessions'),
  revokeSession: (sessionId: number) => deleteAPI(`/api/user/sessions/${sessionId}`),
};

export interface AdminSystemHealthResponse {
  status: string;
  updatedAt: string;
  region?: string;
  services: Array<{
    id: string;
    name: string;
    status: string;
    lastCheckedAt?: string;
    details?: Record<string, unknown>;
  }>;
}

export interface AdminLogRecord {
  id: string;
  type: string;
  title: string;
  severity: string;
  message: string;
  scope?: string;
  scopeId?: string;
  actor?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface AdminLogsResponse {
  items: AdminLogRecord[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  attestationManifestUrl?: string;
}

export interface AdminLogFilters {
  actor?: string;
  action?: string;
  severity?: string;
  start?: string;
  end?: string;
}

export const adminAPI = {
  getStats: () => fetchAPI<any>('/api/admin/stats'),
  getUsers: () => fetchAPI<AdminUserRecord[]>('/api/admin/users'),
  getLogs: (params?: AdminLogFilters) =>
    fetchAPI<AdminLogsResponse>(
      `/api/admin/logs${buildQueryString(params as Record<string, unknown> | undefined)}`,
    ),
  getSystemHealth: () => fetchAPI<AdminSystemHealthResponse>('/api/admin/system/health'),
  getSystemConfigs: () => fetchAPI<AdminSystemConfigRecord[]>('/api/admin/system/configs'),
};

type SettingsQueryParams = {
  orgId?: string;
  userId?: string;
};

const buildSettingsQuery = (params?: SettingsQueryParams): string => {
  if (!params) {
    return '';
  }
  const search = new URLSearchParams();
  if (params.orgId) {
    search.append('orgId', params.orgId);
  }
  if (params.userId) {
    search.append('userId', params.userId);
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const settingsAPI = {
  get: (params?: SettingsQueryParams) =>
    fetchAPI<SettingsResponse>(`/api/settings${buildSettingsQuery(params)}`),
  update: (data: SettingsUpdateRequestPayload) =>
    patchAPI<SettingsUpdateResponse>('/api/settings', data),
};

// Reports & help
export const reportsAPI = {
  getDashboard: (range: string = '30d') => fetchAPI<any>(`/api/reports/dashboard?range=${range}`),
  getByType: (type: string) => fetchAPI<any>(`/api/reports/${type}`),
  generate: (type: string, params: any) =>
    postAPI<any>('/api/reports/generate', { type, ...params }),
};

export const helpAPI = {
  getArticles: () => fetchAPI<any[]>('/api/help/articles'),
  getFAQs: () => fetchAPI<any[]>('/api/help/faqs'),
};

// Accounting (GL, P&L, Balance Sheet)
// Tenant context is injected via x-tenant-id / x-tenant-uuid headers by buildHeaders().
// The gateway resolves the tenant UUID before proxying to the accounting service.
export const accountingAPI = {
  getAccounts: () => fetchAPI<any[]>('/accounting/accounts'),
  seedAccounts: () => postAPI<any>('/accounting/accounts/seed'),
  getJournalEntries: (params?: {
    date_from?: string;
    date_to?: string;
    source_type?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.date_from) qs.set('date_from', params.date_from);
    if (params?.date_to) qs.set('date_to', params.date_to);
    if (params?.source_type) qs.set('source_type', params.source_type);
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchAPI<any[]>(`/accounting/journal-entries${suffix}`);
  },
  getJournalEntry: (entryId: string) => fetchAPI<any>(`/accounting/journal-entries/${entryId}`),
  getTrialBalance: (periodEnd: string) =>
    fetchAPI<{ rows: any[] }>(`/accounting/trial-balance?period_end=${periodEnd}`),
  getPnL: (dateTo: string, dateFrom?: string) => {
    const qs = new URLSearchParams({ date_to: dateTo });
    if (dateFrom) qs.set('date_from', dateFrom);
    return fetchAPI<any>(`/accounting/reports/pnl?${qs.toString()}`);
  },
  getBalanceSheet: (asOf: string) =>
    fetchAPI<any>(`/accounting/reports/balance-sheet?as_of=${asOf}`),
};

export interface OverviewDashboardParams {
  range?: string;
  from?: string;
  to?: string;
  organizationId?: string;
  portfolioId?: string;
  mode?: string;
}

export const overviewAPI = {
  getDashboard: (params?: OverviewDashboardParams) =>
    fetchAPI<any>(
      `/api/overview${buildQueryString(params as Record<string, unknown> | undefined)}`,
    ),
};

// Kill switch
export const resolveApiPath = (endpoint: string): string => buildUrl(endpoint);

export const killSwitchAPI = {
  basePath: resolveApiPath('/api/admin/kill-switch'),
  getStatus: () => fetchAPI<KillSwitchState>(resolveApiPath('/api/admin/kill-switch/status')),
  engage: (data: KillSwitchEngageRequest) =>
    postAPI<KillSwitchState>(resolveApiPath('/api/admin/kill-switch/engage'), data),
  clear: (request: KillSwitchClearRequest) =>
    postAPI<KillSwitchState>(resolveApiPath('/api/admin/kill-switch/clear'), request),
  canUse: () => Boolean(import.meta.env.VITE_API_BASE_URL),
};

// Misc
export interface KPIListParams {
  includeHistory?: boolean;
  historyLimit?: number;
}

export interface KPIUpsertPayload {
  id: string;
  label: string;
  value: number;
  change?: number | null;
  comparison: string;
  icon: string;
  type: KPITypes;
  thresholds?: Record<string, number> | null;
  description?: string | null;
  sort_order?: number | null;
  recorded_at?: string | null;
}

const buildQueryString = (params: Record<string, unknown> | undefined): string => {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null,
  );
  if (entries.length === 0) return '';
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    search.set(
      key.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`),
      String(value),
    );
  }
  return `?${search.toString()}`;
};

const serializeKPIInput = (payload: KPIUpsertPayload): KPIUpsertPayload => {
  const sanitized: KPIUpsertPayload = {
    id: payload.id,
    label: payload.label,
    value: Number(payload.value),
    comparison: payload.comparison,
    icon: payload.icon,
    type: payload.type,
  };

  if (payload.change !== undefined) {
    sanitized.change = payload.change === null ? null : Number(payload.change);
  }
  if (payload.thresholds) {
    sanitized.thresholds = payload.thresholds;
  } else if (payload.thresholds === null) {
    sanitized.thresholds = null;
  }
  if (payload.description !== undefined) {
    sanitized.description = payload.description;
  }
  if (payload.sort_order !== undefined) {
    sanitized.sort_order = payload.sort_order;
  }
  if (payload.recorded_at) {
    sanitized.recorded_at = payload.recorded_at;
  }

  return sanitized;
};

export const kpiAPI = {
  list: (params?: KPIListParams) =>
    fetchAPI<KPI[]>(
      `/api/kpis${buildQueryString({
        includeHistory: params?.includeHistory,
        historyLimit: params?.historyLimit,
      })}`,
    ),
  getById: (id: string, params?: KPIListParams) =>
    fetchAPI<KPI>(
      `/api/kpis/${id}${buildQueryString({
        includeHistory: params?.includeHistory,
        historyLimit: params?.historyLimit,
      })}`,
    ),
  getHistory: (id: string, limit: number = 30) =>
    fetchAPI<KPIHistoryPoint[]>(`/api/kpis/${id}/history${buildQueryString({ limit })}`),
  create: (payload: KPIUpsertPayload) => postAPI<KPI>('/api/kpis', serializeKPIInput(payload)),
  update: (id: string, payload: KPIUpsertPayload) =>
    patchAPI<KPI>(`/api/kpis/${id}`, serializeKPIInput(payload)),
  publish: (id: string) => postAPI<KPI>(`/api/kpis/${id}/publish`),
  retire: (id: string, reason?: string | null) =>
    postAPI<KPI>(`/api/kpis/${id}/retire`, reason ? { reason } : {}),
  remove: (id: string) => deleteAPI(`/api/kpis/${id}`),
};

export const activityAPI = {
  getAll: () => fetchAPI<any[]>('/api/activities'),
};

export const api = {
  get: async <T = any>(endpoint: string) => ({ data: await fetchAPI<T>(endpoint) }),
  post: async <T = any>(endpoint: string, data?: any) => ({
    data: await postAPI<T>(endpoint, data),
  }),
  patch: async <T = any>(endpoint: string, data: any) => ({
    data: await patchAPI<T>(endpoint, data),
  }),
  delete: async (endpoint: string) => deleteAPI(endpoint),
};
